import { createContext, useCallback, useContext, useMemo, type PropsWithChildren } from "react";

import { useCurrentUser } from "../hooks/useCurrentUser";
import { useLocalStorageState } from "../hooks/useLocalStorageState";

type SearchPillsContextValue = {
  /** Whether the search bar renders applied filters as pills. */
  enabled: boolean;
  /** Persist the preference (no-op for non-staff). */
  setEnabled: (enabled: boolean) => void;
  /** Whether the current user may toggle the preview (staff only). */
  canToggle: boolean;
};

const SearchPillsContext = createContext<SearchPillsContextValue>({
  enabled: false,
  setEnabled: () => {},
  canToggle: false,
});

const STORAGE_KEY = "encamp-search-pills";

/**
 * Owns the search-pills preview preference.
 *
 * Customers always get the shipped search bar. Staff can turn pills on to try
 * them, persisted to localStorage. Keyed off `isStaff` rather than
 * `isOriginalUserStaff`, so a staff user emulating a customer sees what that
 * customer sees.
 *
 * Must sit INSIDE CurrentUserProvider (needs `isStaff`).
 */
export function SearchPillsProvider({ children }: PropsWithChildren) {
  const { isStaff } = useCurrentUser();
  const [stored, setStored] = useLocalStorageState<boolean>(STORAGE_KEY, {
    defaultValue: false,
  });

  const enabled = isStaff ? stored : false;

  const setEnabled = useCallback(
    (next: boolean) => {
      if (isStaff) {
        setStored(next);
      }
    },
    [isStaff, setStored]
  );

  const value = useMemo<SearchPillsContextValue>(
    () => ({ enabled, setEnabled, canToggle: Boolean(isStaff) }),
    [enabled, setEnabled, isStaff]
  );

  return <SearchPillsContext.Provider value={value}>{children}</SearchPillsContext.Provider>;
}

export function useSearchPills(): SearchPillsContextValue {
  return useContext(SearchPillsContext);
}
